import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../../utils/fetchInterceptor';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface Game {
  _id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  platform: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  ageRange: string;
  estimatedTime: string;
  gameType: 'Adventure' | 'Puzzle' | 'Competition' | 'Creative' | 'Simulation' | 'Multiplayer';
  isActive: boolean;
  isFeatured: boolean;
  popularity: number;
  rating: number;
  playCount: number;
  completionRate: number;
  averagePlayTime: number;
  bannerImage: string;
  screenshots: string[];
  demoVideo: string;
  tags: string[];
  learningObjectives: string[];
  skills: string[];
  hardware: string[];
  software: string[];
  gameMechanics: string[];
  story: string;
  rules: string[];
  scoring: string;
  progression: {
    levels: number;
    milestones: string[];
    unlockables: string[];
  };
  socialFeatures: {
    multiplayer: boolean;
    sharing: boolean;
    collaboration: boolean;
    competition: boolean;
  };
  accessibility: {
    visual: boolean;
    auditory: boolean;
    motor: boolean;
    cognitive: boolean;
  };
  setup: string[];
  gameplay: string[];
  challenges: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
  achievements: {
    name: string;
    description: string;
    requirements: string;
    reward: string;
  }[];
  leaderboards: {
    type: string;
    description: string;
  }[];
  resources: {
    tutorials: string[];
    videos: string[];
    documentation: string[];
    community: string[];
  };
  safety: string[];
  troubleshooting: {
    commonIssues: string[];
    solutions: string[];
  };
  extensions: string[];
  realWorldApplications: string[];
  educatorNotes: string;
  parentGuide: string;
  difficultyProgression: {
    level: number;
    requirements: string[];
    rewards: string[];
  }[];
  adaptiveLearning: {
    enabled: boolean;
    algorithm: string;
    personalization: string[];
  };
  analytics: {
    tracking: string[];
    metrics: string[];
    insights: string[];
  };
}

const Games: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [formData, setFormData] = useState<Partial<Game>>({
    title: '',
    description: '',
    shortDescription: '',
    category: '',
    platform: '',
    difficulty: 'Easy',
    ageRange: '',
    estimatedTime: '',
    learningObjectives: [],
    skills: [],
    hardware: [],
    software: [],
    gameType: 'Adventure',
    gameMechanics: [],
    story: '',
    rules: [],
    scoring: '',
    progression: {
      levels: 1,
      milestones: [],
      unlockables: []
    },
    socialFeatures: {
      multiplayer: false,
      sharing: false,
      collaboration: false,
      competition: false
    },
    accessibility: {
      visual: false,
      auditory: false,
      motor: false,
      cognitive: false
    },
    setup: [],
    gameplay: [],
    challenges: {
      beginner: [],
      intermediate: [],
      advanced: []
    },
    achievements: [],
    leaderboards: [],
    resources: {
      tutorials: [],
      videos: [],
      documentation: [],
      community: []
    },
    safety: [],
    troubleshooting: {
      commonIssues: [],
      solutions: []
    },
    extensions: [],
    realWorldApplications: [],
    educatorNotes: '',
    parentGuide: '',
    tags: [],
    bannerImage: '',
    screenshots: [],
    demoVideo: '',
    isActive: true,
    isFeatured: false,
    popularity: 0,
    rating: 0,
    playCount: 0,
    completionRate: 0,
    averagePlayTime: 0,
    difficultyProgression: [],
    adaptiveLearning: {
      enabled: false,
      algorithm: '',
      personalization: []
    },
    analytics: {
      tracking: [],
      metrics: [],
      insights: []
    }
  });

  const fetchGames = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors
      const response = await fetch(`${process.env.REACT_APP_API_URL}/highcode/games`);
      if (response.ok) {
        const data = await response.json();
        setGames(data);
      } else {
        setError('Failed to fetch games');
      }
    } catch (err) {
      setError('Error fetching games');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const handleAddGame = async () => {
    try {
      setIsSaving(true);
      setError(null); // Clear any previous errors
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/highcode/games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response && response.ok) {
        await fetchGames();
        setShowAddModal(false);
        setError(null); // Clear error on success
        setFormData({
          title: '',
          description: '',
          shortDescription: '',
          category: '',
          platform: '',
          difficulty: 'Easy',
          ageRange: '',
          estimatedTime: '',
          learningObjectives: [],
          skills: [],
          hardware: [],
          software: [],
          gameType: 'Adventure',
          gameMechanics: [],
          story: '',
          rules: [],
          scoring: '',
          progression: {
            levels: 1,
            milestones: [],
            unlockables: []
          },
          socialFeatures: {
            multiplayer: false,
            sharing: false,
            collaboration: false,
            competition: false
          },
          accessibility: {
            visual: false,
            auditory: false,
            motor: false,
            cognitive: false
          },
          setup: [],
          gameplay: [],
          challenges: {
            beginner: [],
            intermediate: [],
            advanced: []
          },
          achievements: [],
          leaderboards: [],
          resources: {
            tutorials: [],
            videos: [],
            documentation: [],
            community: []
          },
          safety: [],
          troubleshooting: {
            commonIssues: [],
            solutions: []
          },
          extensions: [],
          realWorldApplications: [],
          educatorNotes: '',
          parentGuide: '',
          tags: [],
          bannerImage: '',
          screenshots: [],
          demoVideo: '',
          isActive: true,
          isFeatured: false,
          popularity: 0,
          rating: 0,
          playCount: 0,
          completionRate: 0,
          averagePlayTime: 0,
          difficultyProgression: [],
          adaptiveLearning: {
            enabled: false,
            algorithm: '',
            personalization: []
          },
          analytics: {
            tracking: [],
            metrics: [],
            insights: []
          }
        });
      } else {
        setError('Failed to create game');
      }
    } catch (err) {
      setError('Error creating game');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditGame = async () => {
    if (!selectedGame) return;
    try {
      setIsUpdating(true);
      setError(null); // Clear any previous errors
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/highcode/games/${selectedGame._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response && response.ok) {
        await fetchGames();
        setShowEditModal(false);
        setSelectedGame(null);
        setError(null); // Clear error on success
      } else {
        setError('Failed to update game');
      }
    } catch (err) {
      setError('Error updating game');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteGame = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    try {
      setIsDeleting(true);
      setError(null); // Clear any previous errors
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/highcode/games/${id}`, {
        method: 'DELETE',
      });

      if (response && response.ok) {
        await fetchGames();
        setError(null); // Clear error on success
      } else {
        setError('Failed to delete game');
      }
    } catch (err) {
      setError('Error deleting game');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewGame = (game: Game) => {
    setSelectedGame(game);
    setShowViewModal(true);
  };

  const handleEditGameClick = (game: Game) => {
    setSelectedGame(game);
    setFormData(game);
    setShowEditModal(true);
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setSelectedGame(null);
    setFormData({
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      platform: '',
      difficulty: 'Easy',
      ageRange: '',
      estimatedTime: '',
      learningObjectives: [],
      skills: [],
      hardware: [],
      software: [],
      gameType: 'Adventure',
      gameMechanics: [],
      story: '',
      rules: [],
      scoring: '',
      progression: {
        levels: 1,
        milestones: [],
        unlockables: []
      },
      socialFeatures: {
        multiplayer: false,
        sharing: false,
        collaboration: false,
        competition: false
      },
      accessibility: {
        visual: false,
        auditory: false,
        motor: false,
        cognitive: false
      },
      setup: [],
      gameplay: [],
      challenges: {
        beginner: [],
        intermediate: [],
        advanced: []
      },
      achievements: [],
      leaderboards: [],
      resources: {
        tutorials: [],
        videos: [],
        documentation: [],
        community: []
      },
      safety: [],
      troubleshooting: {
        commonIssues: [],
        solutions: []
      },
      extensions: [],
      realWorldApplications: [],
      educatorNotes: '',
      parentGuide: '',
      tags: [],
      bannerImage: '',
      screenshots: [],
      demoVideo: '',
      isActive: true,
      isFeatured: false,
      popularity: 0,
      rating: 0,
      playCount: 0,
      completionRate: 0,
      averagePlayTime: 0,
      difficultyProgression: [],
      adaptiveLearning: {
        enabled: false,
        algorithm: '',
        personalization: []
      },
      analytics: {
        tracking: [],
        metrics: [],
        insights: []
      }
    });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Games Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-plus mr-2"></i>
          Add New Game
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Game
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Difficulty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Range
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {games.map((game) => (
                <tr key={game._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={game.bannerImage}
                          alt={game.title}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {game.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {game.shortDescription}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {game.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {game.platform}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      game.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                      game.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {game.difficulty}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {game.ageRange}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        game.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {game.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {game.isFeatured && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Featured
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewGame(game)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        onClick={() => handleEditGameClick(game)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteGame(game._id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={isDeleting}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Game Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showAddModal ? 'Add New Game' : 'Edit Game'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform
                  </label>
                  <select
                    value={formData.platform || ''}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Platform</option>
                    <option value="Arduino">Arduino</option>
                    <option value="Raspberry Pi">Raspberry Pi</option>
                    <option value="ESP32">ESP32</option>
                    <option value="ESP8266">ESP8266</option>
                    <option value="Micro:bit">Micro:bit</option>
                    <option value="Scratch">Scratch</option>
                    <option value="Python">Python</option>
                    <option value="JavaScript">JavaScript</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty || 'Easy'}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as 'Easy' | 'Medium' | 'Hard' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age Range
                  </label>
                  <input
                    type="text"
                    value={formData.ageRange || ''}
                    onChange={(e) => setFormData({ ...formData, ageRange: e.target.value })}
                    placeholder="e.g., 6-8, 9-12, 13-16"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Time
                  </label>
                  <input
                    type="text"
                    value={formData.estimatedTime || ''}
                    onChange={(e) => setFormData({ ...formData, estimatedTime: e.target.value })}
                    placeholder="e.g., 30 minutes, 2 hours"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Game Type
                  </label>
                  <select
                    value={formData.gameType || 'Adventure'}
                    onChange={(e) => setFormData({ ...formData, gameType: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Adventure">Adventure</option>
                    <option value="Puzzle">Puzzle</option>
                    <option value="Competition">Competition</option>
                    <option value="Creative">Creative</option>
                    <option value="Simulation">Simulation</option>
                    <option value="Multiplayer">Multiplayer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banner Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.bannerImage || ''}
                    onChange={(e) => setFormData({ ...formData, bannerImage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Short Description
                </label>
                <textarea
                  value={formData.shortDescription || ''}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Story
                </label>
                <textarea
                  value={formData.story || ''}
                  onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scoring System
                </label>
                <textarea
                  value={formData.scoring || ''}
                  onChange={(e) => setFormData({ ...formData, scoring: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Educator Notes
                </label>
                <textarea
                  value={formData.educatorNotes || ''}
                  onChange={(e) => setFormData({ ...formData, educatorNotes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Guide
                </label>
                <textarea
                  value={formData.parentGuide || ''}
                  onChange={(e) => setFormData({ ...formData, parentGuide: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4 flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive || false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured || false}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                    className="mr-2"
                  />
                  Featured
                </label>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCloseModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={showAddModal ? handleAddGame : handleEditGame}
                  disabled={isSaving || isUpdating}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving || isUpdating ? 'Saving...' : (showAddModal ? 'Add Game' : 'Update Game')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Game Modal */}
      {showViewModal && selectedGame && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Game Details: {selectedGame.title}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.title}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.category}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.platform}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.difficulty}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age Range
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.ageRange}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Time
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.estimatedTime}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Game Type
                  </label>
                  <p className="text-sm text-gray-900">{selectedGame.gameType}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="flex space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedGame.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedGame.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {selectedGame.isFeatured && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <p className="text-sm text-gray-900">{selectedGame.description}</p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Story
                </label>
                <p className="text-sm text-gray-900">{selectedGame.story}</p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scoring System
                </label>
                <p className="text-sm text-gray-900">{selectedGame.scoring}</p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Learning Objectives
                </label>
                <ul className="text-sm text-gray-900 list-disc list-inside">
                  {selectedGame.learningObjectives.map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Skills
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedGame.skills.map((skill, index) => (
                    <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hardware Required
                </label>
                <ul className="text-sm text-gray-900 list-disc list-inside">
                  {selectedGame.hardware.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Software Required
                </label>
                <ul className="text-sm text-gray-900 list-disc list-inside">
                  {selectedGame.software.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game Mechanics
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedGame.gameMechanics.map((mechanic, index) => (
                    <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {mechanic}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Social Features
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedGame.socialFeatures.multiplayer && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      Multiplayer
                    </span>
                  )}
                  {selectedGame.socialFeatures.sharing && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      Sharing
                    </span>
                  )}
                  {selectedGame.socialFeatures.collaboration && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      Collaboration
                    </span>
                  )}
                  {selectedGame.socialFeatures.competition && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      Competition
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Accessibility Features
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedGame.accessibility.visual && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Visual
                    </span>
                  )}
                  {selectedGame.accessibility.auditory && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Auditory
                    </span>
                  )}
                  {selectedGame.accessibility.motor && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Motor
                    </span>
                  )}
                  {selectedGame.accessibility.cognitive && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Cognitive
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Educator Notes
                </label>
                <p className="text-sm text-gray-900">{selectedGame.educatorNotes}</p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Guide
                </label>
                <p className="text-sm text-gray-900">{selectedGame.parentGuide}</p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;
